import { useState } from 'react';
import { LockIcon } from '../components/UltimateIcons';
import { useFcdLang, useFcdText } from './fcdText';
import {
  FILL_ORDER,
  isLocked,
  MONTHLY_FILL_LABEL,
  MONTHLY_FILL_LABEL_KO,
  MONTHLY_SECTIONS,
  monthlyFillCounts,
  type MonthlyFill,
} from './monthlySections';

/**
 * The Monthly tab (SSD-7602): every section and row of the monthly review, in the Notion page's order, each saying how
 * its number gets filled. No number is wired in yet, so every row reads WIP; cost and people rows wait for the decision
 * on who may see them. A fill chip narrows the rows to that kind; the section list beside them jumps to a section.
 */
export function MonthlyTab() {
  const text = useFcdText();
  const lang = useFcdLang();
  const [fill, setFill] = useState<MonthlyFill | null>(null);
  const counts = monthlyFillCounts();
  const fillLabel = lang === 'ko' ? MONTHLY_FILL_LABEL_KO : MONTHLY_FILL_LABEL;
  const totalRows = MONTHLY_SECTIONS.reduce((sum, section) => sum + section.rows.length, 0);
  const lockedRows = MONTHLY_SECTIONS.reduce(
    (sum, section) => sum + section.rows.filter((row) => isLocked(section, row)).length,
    0,
  );
  const shown = MONTHLY_SECTIONS.map((section) => ({
    section,
    title: lang === 'ko' ? section.titleKo : section.title,
    rows: section.rows.filter((row) => fill === null || row.fill === fill),
  }));

  const jumpTo = (no: number) =>
    document.getElementById(`fcd-month-${no}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  return (
    <div className="fcd-monthly">
      <section className="fcd-month-summary" aria-label={text.monthlyTitle}>
        <div className="fcd-month-top">
          <div className="fcd-month-lede">
            <h3>{text.monthlyTitle}</h3>
            <p>{text.monthlyIntro}</p>
          </div>
          <dl className="fcd-month-stats">
            <div>
              <dt>{text.monthlyStatRows}</dt>
              <dd>{totalRows}</dd>
            </div>
            <div>
              <dt>{text.monthlyStatWired}</dt>
              <dd>0</dd>
            </div>
            <div>
              <dt>{text.monthlyStatLocked}</dt>
              <dd>{lockedRows}</dd>
            </div>
          </dl>
        </div>
        <div className="fcd-fillbar" aria-hidden="true">
          {FILL_ORDER.map((kind) => (
            <span
              key={kind}
              className={`fcd-fillbar-seg fill-${kind.toLowerCase()}${fill !== null && fill !== kind ? ' is-dim' : ''}`}
              style={{ flexGrow: counts[kind] }}
            />
          ))}
        </div>
        <div className="fcd-legend" role="group" aria-label={text.monthlyFilter}>
          <button
            type="button"
            className={fill === null ? 'fcd-filter on' : 'fcd-filter'}
            aria-pressed={fill === null}
            onClick={() => setFill(null)}
          >
            {text.monthlyAll} <b>{totalRows}</b>
          </button>
          {FILL_ORDER.map((kind) => (
            <button
              type="button"
              key={kind}
              className={`fcd-filter fill-${kind.toLowerCase()}${fill === kind ? ' on' : ''}`}
              aria-pressed={fill === kind}
              onClick={() => setFill(fill === kind ? null : kind)}
            >
              <i className="fcd-dot" aria-hidden="true" />
              {fillLabel[kind]} <b>{counts[kind]}</b>
            </button>
          ))}
        </div>
      </section>

      <div className="fcd-month-layout">
        <nav className="fcd-month-toc" aria-label={text.monthlySections}>
          {shown.map(({ section, title, rows }) => (
            <button
              type="button"
              key={section.no}
              className={rows.length === 0 ? 'fcd-toc-item is-empty' : 'fcd-toc-item'}
              disabled={rows.length === 0}
              onClick={() => jumpTo(section.no)}
            >
              <span className="fcd-step">{section.no}</span>
              <span className="fcd-toc-title">{title}</span>
              {section.restricted && <LockIcon className="fcd-toc-lock" />}
              <span className="fcd-toc-n">{rows.length}</span>
            </button>
          ))}
        </nav>

        <div className="fcd-month-sections">
          {shown.every(({ rows }) => rows.length === 0) && <div className="fcd-state">{text.monthlyNoMatch}</div>}
          {shown.map(({ section, title, rows }) =>
            rows.length === 0 ? null : (
              <section
                key={section.no}
                id={`fcd-month-${section.no}`}
                className="fcd-month-card"
                aria-label={`${section.no}. ${title}`}
              >
                <div className="fcd-month-card-head">
                  <span className="fcd-step">{section.no}</span>
                  <h3>{title}</h3>
                  <span className="fcd-count">{text.monthlyRows(rows.length)}</span>
                  <span className="sp" />
                  {section.restricted && (
                    <span className="fcd-lockpill">
                      <LockIcon />
                      {text.monthlyLockedNote}
                    </span>
                  )}
                </div>
                <div className="tbl-scroll">
                  <table className="tbl fcd-month" aria-label={`${section.no}. ${title}`}>
                    <colgroup>
                      <col />
                      <col className="col-fill" />
                      <col className="col-source" />
                      <col className="col-value" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>{text.monthlyColRow}</th>
                        <th>{text.monthlyColFill}</th>
                        <th>{text.monthlyColSource}</th>
                        <th>{text.monthlyColValue}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const locked = isLocked(section, row);
                        return (
                          <tr key={row.label} className={`fill-row fill-${row.fill.toLowerCase()}`}>
                            <td className="fcd-row-label">{lang === 'ko' ? row.labelKo : row.label}</td>
                            <td>
                              <span className={`fcd-fill fill-${row.fill.toLowerCase()}`}>
                                <i className="fcd-dot" aria-hidden="true" />
                                {fillLabel[row.fill]}
                              </span>
                            </td>
                            <td className="fcd-source">{lang === 'ko' ? row.sourceKo : row.source}</td>
                            <td>
                              {locked ? (
                                <span className="fcd-wip is-locked">
                                  <LockIcon />
                                  {text.monthlyLockedChip}
                                </span>
                              ) : (
                                <span className="fcd-wip">{text.wip}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
